import { MediaRegistry } from './MediaRegistry';
import { VoiceoverRegistry } from './VoiceoverRegistry';

import type { StoredUploadedAssetData } from 'js/src/data/uploads/uploads-selectors';

/**
 * Resolves the short registry id the agent works with into the asset UUID that the
 * upload/transcript APIs need.
 *
 * The agent only ever sees registry ids (`### AUDIO: … | ID: … ###`, search results, media
 * tools), so tools take an id and translate here. Audio is looked up in the VoiceoverRegistry
 * and video in the MediaRegistry, since an "audio" input may legitimately be a talking-head
 * video.
 *
 * Registry ids only — an asset UUID is not accepted and returns null, so a stale or
 * hallucinated identifier fails loudly instead of reaching the APIs.
 */
export function resolveAssetUuidFromRegistryId(identifier: string): string | null {
    const voiceoverRegistry = VoiceoverRegistry.getInstance();
    if (voiceoverRegistry.hasItem(identifier)) {
        const uuid = voiceoverRegistry.getItem(identifier)?.audio?.uuid;
        if (uuid) {
            return uuid;
        }
    }

    const mediaRegistry = MediaRegistry.getInstance();
    if (mediaRegistry.hasItem(identifier)) {
        const background = mediaRegistry.getItem(identifier)?.background;
        const uuid = background?.video?.uuid ?? background?.image?.uuid;
        if (uuid) {
            return uuid;
        }
    }

    return null;
}

/**
 * Returns the asset a registry id names, without re-fetching it: the id only exists because the
 * asset is already registered. Audio comes back as the AudioAssetModel shape and video as the
 * SlideBackgroundModel shape, matching what `getUploadedAssetByUuid` yields so callers can treat
 * the two sources interchangeably. Null when the id names nothing.
 */
export function getRegisteredAssetByRegistryId(identifier: string): StoredUploadedAssetData | null {
    const voiceoverRegistry = VoiceoverRegistry.getInstance();
    if (voiceoverRegistry.hasItem(identifier)) {
        const audioData = voiceoverRegistry.getAudioAsset(identifier);
        if (audioData) {
            return audioData;
        }
    }

    return MediaRegistry.getInstance().getItem(identifier)?.background ?? null;
}
